<script setup lang="ts">
import { reactive, computed } from 'vue'
import { Button } from 'ui-kit'
import type { SchoolColorThemeData } from '@/composables/api/useSchoolColorThemesApi'

interface Props {
  // When editing an existing theme, pass its current values to prefill.
  initial?: Partial<SchoolColorThemeData>
  saving?: boolean
  submitLabel?: string
}
const props = withDefaults(defineProps<Props>(), {
  saving: false,
  submitLabel: 'Save theme',
})

const emit = defineEmits<{
  (e: 'submit', value: SchoolColorThemeData): void
  (e: 'cancel'): void
}>()

// Sensible defaults so a brand-new theme is valid without touching every field.
const DEFAULTS: SchoolColorThemeData = {
  name: '',
  bulletin_badge: '#e8f0fe',
  card_background: '#ffffff',
  table_time_background: '#f5f5f5',
  table_background: '#ffffff',
  table_header_color: '#333333',
  primary_header: '#001f3f',
  table_day_cell: '#e8f0fe',
  table_white_stripes: '#ffffff',
  table_dark_stripes: '#fafafa',
  default_btn_hover: '#002b56',
  sidebar_bg: '#001f3f',
  icon_color: '#ffffff',
  sidebar_hover_bg: '#002b56',
  sidebar_hover_color: '#ffffff',
  logout_hover_bg: '#cc0000',
  table_row_hover: '#f0f0f0',
  progress_bg: '#e5e7eb',
  progress_color: '#001f3f',
  default_btn: '#001f3f',
  ghost_btn_hover: '',
  ghost_btn_color: '',
  hover_input_border: '',
  statement_bg: '',
  statement_border: '',
  statement_color: '',
}

const form = reactive<SchoolColorThemeData>({ ...DEFAULTS, ...(props.initial ?? {}) })

// Field groups for the editor layout. Each field is one color.
type Field = { key: keyof SchoolColorThemeData; label: string }
const groups: { title: string; fields: Field[] }[] = [
  {
    title: 'Primary',
    fields: [
      { key: 'primary_header', label: 'Primary header' },
      { key: 'card_background', label: 'Card background' },
      { key: 'bulletin_badge', label: 'Bulletin badge' },
    ],
  },
  {
    title: 'Sidebar',
    fields: [
      { key: 'sidebar_bg', label: 'Sidebar background' },
      { key: 'icon_color', label: 'Icon color' },
      { key: 'sidebar_hover_bg', label: 'Sidebar hover bg' },
      { key: 'sidebar_hover_color', label: 'Sidebar hover color' },
      { key: 'logout_hover_bg', label: 'Logout hover bg' },
    ],
  },
  {
    title: 'Tables',
    fields: [
      { key: 'table_background', label: 'Table background' },
      { key: 'table_header_color', label: 'Table header' },
      { key: 'table_time_background', label: 'Table time bg' },
      { key: 'table_day_cell', label: 'Table day cell' },
      { key: 'table_white_stripes', label: 'Table white stripes' },
      { key: 'table_dark_stripes', label: 'Table dark stripes' },
      { key: 'table_row_hover', label: 'Table row hover' },
    ],
  },
  {
    title: 'Buttons & progress',
    fields: [
      { key: 'default_btn', label: 'Default button' },
      { key: 'default_btn_hover', label: 'Default button hover' },
      { key: 'progress_bg', label: 'Progress bg' },
      { key: 'progress_color', label: 'Progress color' },
    ],
  },
  {
    title: 'Optional',
    fields: [
      { key: 'ghost_btn_hover', label: 'Ghost button hover' },
      { key: 'ghost_btn_color', label: 'Ghost button color' },
      { key: 'hover_input_border', label: 'Input hover border' },
      { key: 'statement_bg', label: 'Statement bg' },
      { key: 'statement_border', label: 'Statement border' },
      { key: 'statement_color', label: 'Statement color' },
    ],
  },
]

// A value is a plain hex if it looks like #rrggbb — only then can the native
// color picker represent it. Gradients/rgba stay editable via the text field.
function isHex(v: string | undefined): boolean {
  return !!v && /^#[0-9a-fA-F]{6}$/.test(v)
}

const canSubmit = computed(() => form.name.trim().length > 0)

function onSubmit() {
  if (!canSubmit.value) return
  // Drop empty optional fields so we don't send blank strings.
  const payload: SchoolColorThemeData = { ...form }
  ;(
    [
      'ghost_btn_hover',
      'ghost_btn_color',
      'hover_input_border',
      'statement_bg',
      'statement_border',
      'statement_color',
    ] as (keyof SchoolColorThemeData)[]
  ).forEach((k) => {
    if (!payload[k]) delete payload[k]
  })
  emit('submit', payload)
}
</script>

<template>
  <form class="theme-editor" @submit.prevent="onSubmit">
    <div class="theme-editor__name">
      <label class="theme-editor__label" for="theme-name">Theme name *</label>
      <input
        id="theme-name"
        v-model="form.name"
        class="theme-editor__text"
        placeholder="e.g. Golden International"
      />
    </div>

    <div v-for="group in groups" :key="group.title" class="theme-editor__group">
      <h4 class="theme-editor__group-title">{{ group.title }}</h4>
      <div class="theme-editor__grid">
        <div v-for="f in group.fields" :key="String(f.key)" class="theme-editor__field">
          <label class="theme-editor__label">{{ f.label }}</label>
          <div class="theme-editor__inputs">
            <input
              v-if="isHex(form[f.key] as string) || !form[f.key]"
              type="color"
              class="theme-editor__color"
              :value="isHex(form[f.key] as string) ? form[f.key] : '#ffffff'"
              @input="form[f.key] = ($event.target as HTMLInputElement).value"
            />
            <span
              v-else
              class="theme-editor__swatch"
              :style="{ background: form[f.key] as string }"
              title="Gradient / non-hex value"
            />
            <input
              v-model="form[f.key] as string"
              class="theme-editor__text theme-editor__text--sm"
              placeholder="#rrggbb or CSS"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="theme-editor__actions">
      <Button variant="outline" type="button" @click="emit('cancel')">Cancel</Button>
      <Button type="submit" :disabled="!canSubmit || saving">
        {{ saving ? 'Saving…' : submitLabel }}
      </Button>
    </div>
  </form>
</template>

<style scoped>
.theme-editor {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.theme-editor__name {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.theme-editor__group-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: #78716c;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.theme-editor__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
.theme-editor__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.theme-editor__label {
  font-size: 12px;
  color: #57534e;
}
.theme-editor__inputs {
  display: flex;
  align-items: center;
  gap: 8px;
}
.theme-editor__color {
  width: 36px;
  height: 32px;
  padding: 0;
  border: 1px solid #d6d3d1;
  border-radius: 6px;
  background: none;
  cursor: pointer;
  flex-shrink: 0;
}
.theme-editor__swatch {
  width: 36px;
  height: 32px;
  border: 1px solid #d6d3d1;
  border-radius: 6px;
  flex-shrink: 0;
}
.theme-editor__text {
  padding: 8px 10px;
  border: 1px solid #d6d3d1;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
}
.theme-editor__text--sm {
  font-size: 12px;
}
.theme-editor__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0efed;
}
</style>
